import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { Trophy, X, CheckCircle2, XCircle } from 'lucide-react'
import type { PendingQuiz } from '@/types'

interface WeeklyQuizNudgeModalProps {
  quiz: PendingQuiz
  onClose: () => void
}

type ModalPhase = 'question' | 'result'

export function WeeklyQuizNudgeModal({ quiz, onClose }: WeeklyQuizNudgeModalProps) {
  const [phase, setPhase] = useState<ModalPhase>('question')
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const toggleAnswer = (index: number) => {
    if (quiz.multiple_choice) {
      setSelectedAnswers(prev =>
        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
      )
    } else {
      setSelectedAnswers([index])
    }
  }

  const handleSubmit = async () => {
    if (selectedAnswers.length === 0) return

    setIsSubmitting(true)

    // Check correctness client-side for instant feedback
    const correct = selectedAnswers.length === quiz.correct_answer_indices.length &&
      selectedAnswers.every(i => quiz.correct_answer_indices.includes(i))
    setIsCorrect(correct)

    // Persist server-side via fetch (non-Inertia to avoid redirect)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const formData = new FormData()
    selectedAnswers.forEach(i => formData.append('selected_answer_indices[]', i.toString()))

    try {
      await fetch(`/defis/quiz/${quiz.id}/submit`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData,
        redirect: 'manual',
      })
    } catch {
      // Submission is best-effort; result is already shown
    }

    setPhase('result')
    setIsSubmitting(false)
  }

  const handleClose = () => {
    if (phase === 'result') {
      // Reload current page to refresh shared props (pending_quiz becomes null)
      router.reload({ only: [] })
    }
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Card */}
      <div
        className="relative z-10 bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-5">
          <div className="mx-auto w-12 h-12 bg-tessan-yellow/30 rounded-full flex items-center justify-center mb-3">
            <Trophy className="w-6 h-6 text-tessan-green" />
          </div>
          <h3 className="text-lg font-bold text-tessan-green">
            Quiz de la semaine
          </h3>
          <p className="text-sm text-gray-500">{quiz.title} &middot; {quiz.week_range}</p>
        </div>

        {phase === 'question' && (
          <>
            {/* Question */}
            <p className="font-semibold text-gray-900 mb-1">{quiz.question}</p>

            {quiz.multiple_choice && (
              <p className="text-sm text-blue-600 font-medium mb-3">
                Plusieurs réponses possibles ({quiz.correct_answer_count} bonnes réponses)
              </p>
            )}

            {/* Answers */}
            <div className="space-y-2 mt-3">
              {quiz.answers.map((answer, index) => (
                <label
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedAnswers.includes(index)
                      ? 'border-tessan-green bg-tessan-cream'
                      : 'border-gray-200 hover:border-tessan-green/50 hover:bg-tessan-cream/50'
                  }`}
                >
                  <input
                    type={quiz.multiple_choice ? 'checkbox' : 'radio'}
                    name="quiz_answer"
                    checked={selectedAnswers.includes(index)}
                    onChange={() => toggleAnswer(index)}
                    className="w-4 h-4 accent-tessan-primary"
                  />
                  <span className="flex-1 text-sm text-gray-900">{answer.text}</span>
                </label>
              ))}
            </div>

            {/* Points hint */}
            <p className="text-xs text-gray-500 mt-3 text-center">
              +{quiz.points_reward} points si bonne réponse
            </p>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={selectedAnswers.length === 0 || isSubmitting}
              className="mt-4 w-full py-2.5 bg-tessan-green text-white font-semibold rounded-lg hover:bg-tessan-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Envoi...' : 'Valider ma réponse'}
            </button>

            {/* Dismiss */}
            <button
              onClick={handleClose}
              className="mt-2 w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Plus tard
            </button>
          </>
        )}

        {phase === 'result' && (
          <>
            {/* Answers with result highlights */}
            <div className="space-y-2">
              {quiz.answers.map((answer, index) => {
                const isUserAnswer = selectedAnswers.includes(index)
                const isCorrectAnswer = quiz.correct_answer_indices.includes(index)

                return (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
                      isCorrectAnswer
                        ? 'bg-tessan-cream border-tessan-green'
                        : isUserAnswer
                        ? 'bg-red-50 border-red-300'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {isCorrectAnswer ? (
                      <CheckCircle2 className="w-5 h-5 text-tessan-green flex-shrink-0" />
                    ) : isUserAnswer ? (
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 flex-shrink-0" />
                    )}
                    <span className="flex-1 text-sm text-gray-900">{answer.text}</span>
                  </div>
                )
              })}
            </div>

            {/* Result feedback */}
            <div className={`mt-4 p-3 rounded-lg text-center ${
              isCorrect ? 'bg-tessan-cream border border-tessan-green/20' : 'bg-red-50 border border-red-200'
            }`}>
              {isCorrect ? (
                <p className="text-tessan-green font-medium flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Bravo ! +{quiz.points_reward} points
                </p>
              ) : (
                <p className="text-red-700 font-medium">
                  Dommage, ce n'était pas la bonne réponse.
                </p>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="mt-4 w-full py-2.5 bg-tessan-green text-white font-semibold rounded-lg hover:bg-tessan-green/90 transition-colors"
            >
              Fermer
            </button>
          </>
        )}
      </div>
    </div>
  )
}
